import { Component, computed, OnDestroy, OnInit, Signal, signal, WritableSignal, inject } from '@angular/core';
import { BoFormsModule } from '@bo/ng-forms';
import { FieldComponent } from '../../../shared/components/forms/form-field/form-field.component';
import { ActivatedRoute, Params, RouterLink } from '@angular/router';
import { SubmitComponent } from '../../../shared/components/forms/submit/submit.component';
import { AppFormComponent } from '../../../shared/classes/app-form';
import { Method } from '@bo/ng-api';
import { Observable } from 'rxjs';
import { PasswordResetService } from '../../../_services/password-reset/password-reset.service';
import { EmailRequest, PasswordResponse } from '../../../_interfaces/password-interface';
import { GoToStep } from '../../../store/onboarding/onboarding.actions';
import { Store } from '@ngxs/store';
import { OnboardingSteps } from '../../../store/onboarding/onboarding.model';
import { RouterSwitchBtnComponent } from '../../../shared/components/router-switch-btn/router-switch-btn.component';
import { ProgressBarService } from '../../../_services/progress-bar-service/progress-bar.service';
import { LoaderComponent } from '../../../shared/components/loader/loader.component';
import { HttpErrorResponse } from '@angular/common/http';
import { getSecondsFromThrottleMsg } from '../../../shared/utils/resend-btn-utils';
import { ResendBtnComponent } from '../../../shared/components/resend-btn/resend-btn.component';

@Component({
  selector: 'app-forgot-password',
  imports: [
    BoFormsModule,
    FieldComponent,
    RouterLink,
    SubmitComponent,
    RouterSwitchBtnComponent,
    LoaderComponent,
    ResendBtnComponent,
  ],
  templateUrl: './forgot-password.component.html',
})
export class ForgotPasswordComponent extends AppFormComponent<PasswordResponse> implements OnInit, OnDestroy {
  readonly store = inject(Store);
  readonly passwordResetService = inject(PasswordResetService);
  readonly progressBarService = inject(ProgressBarService);
  readonly route = inject(ActivatedRoute);

  protected fields: string[] = ['email'];
  onSuccess: boolean = false;
  enteredEmail?: string;
  email?: string;
  emailSentText: string = `We've sent an email to ${this.email}`;
  resend$!: Observable<PasswordResponse>;
  interval?: ReturnType<typeof setTimeout>;

  timeoutSeconds: WritableSignal<number> = signal<number>(0);
  showThrottleWarning: Signal<boolean> = computed<boolean>(() => this.timeoutSeconds() > 0);

  constructor() {
    super();
    this.goToStep(OnboardingSteps.PASSWORD_REQUEST);
    this.subscriptions.add(
      this.route.queryParams.subscribe({
        next: (params: Params) => {
          this.enteredEmail = params['email'];
        },
      }),
    );
  }

  override ngOnInit() {
    super.ngOnInit();
    this.progressBarService.setHideProgress(true);
  }

  // Change svg animation
  goToStep(step: OnboardingSteps) {
    this.store.dispatch(new GoToStep(step));
  }

  // Builds the HTML form with the correct field and links to the API
  createForm(): void {
    this.buildFromOptions(
      this.passwordResetService.getOptions(this.passwordResetService.URL_PASSWORD_REQUEST, Method.POST, true),
    ).subscribe({
      next: () => {
        if (this.enteredEmail) {
          this.form.get('email')?.setValue(this.enteredEmail);
        }
      },
    });
  }

  // On submitting the form
  // Return type of your submitted data
  override submitData(formData: EmailRequest): Observable<PasswordResponse> {
    this.email = formData.email;
    this.emailSentText = `We've sent an email to ${formData.email}.`;
    return this.passwordResetService.requestEmail(formData);
  }

  // On successfully submitting the form
  override handleSubmitSuccess(): void {
    this.onSuccess = true;
    // Create the resend$ observable after email is set
    this.resend$ = this.passwordResetService.requestEmail({ email: this.email! });
    this.goToStep(OnboardingSteps.EMAIL_CHECK);
  }

  clearTimer() {
    if (this.interval) {
      clearInterval(this.interval);
    }
  }

  startTimer() {
    this.clearTimer();
    this.interval = setInterval(() => {
      this.timeoutSeconds.update((value) => value - 1);
      if (this.timeoutSeconds() <= 0) {
        this.clearTimer();
      }
    }, 1000);
  }

  override handleSubmitError(error: unknown): void {
    if (error instanceof HttpErrorResponse && error.status === 429) {
      const message = error.error!['detail'];
      if (message.includes('Request was throttled')) {
        this.timeoutSeconds.set(getSecondsFromThrottleMsg(message));
        this.startTimer();
      }
    } else {
      super.handleSubmitError(error);
    }
  }

  emailResend() {
    this.emailSentText = `We've resent an email to ${this.email}.`;
  }

  override ngOnDestroy() {
    super.ngOnDestroy();
    this.progressBarService.setHideProgress(false);
  }
}
